"use client";

import { setClientToken } from "@onsand/api";
import { auth } from "@onsand/auth";
import { signInWithEmailAndPassword } from "firebase/auth";
import { CircleAlert, Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import { useTranslations } from "next-intl";
import { type FormEvent, useState } from "react";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Label } from "@/components/ui/label";
import { useRouter } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { type SignInErrorKey, signInErrorKey } from "./sign-in-error";

// `group/field` lets the leading icon turn verdigris with the field's focus ring.
const FIELD =
	"group/field h-12 rounded-xl text-base shadow-none sm:text-base **:[input]:h-full **:[input]:placeholder:text-muted-foreground/85";
const FIELD_ICON =
	"size-5 text-muted-foreground transition-colors group-has-[input:focus-visible]/field:text-verdigris-bright";
const LABEL = "text-base/5 sm:text-base/5";

export function LoginForm({ className }: { className?: string }) {
	const router = useRouter();
	const t = useTranslations("login");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState<SignInErrorKey | null>(null);
	const [loading, setLoading] = useState(false);

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setLoading(true);
		setError(null);

		try {
			const credential = await signInWithEmailAndPassword(auth, email, password);
			setClientToken(await credential.user.getIdToken());
			router.push("/");
		} catch (err) {
			setError(signInErrorKey(err));
		} finally {
			setLoading(false);
		}
	}

	return (
		<section
			aria-labelledby="login-title"
			className={cn(
				"flex flex-col justify-center bg-card px-6 py-10 sm:px-10 lg:px-16 lg:py-16",
				className,
			)}
		>
			<div className="mx-auto w-full max-w-100">
				<h1
					className="text-balance font-[650] text-[2rem] leading-[1.08] tracking-[-0.02em]"
					id="login-title"
				>
					{t("title")}
				</h1>
				<p className="mt-2 text-base text-muted-foreground">{t("signInDescription")}</p>

				<form className="mt-8 grid gap-5" onSubmit={handleSubmit}>
					<div className="grid gap-2">
						<Label className={LABEL} htmlFor="email">
							{t("email")}
						</Label>
						<InputGroup className={FIELD}>
							<InputGroupAddon>
								<Mail aria-hidden="true" className={FIELD_ICON} />
							</InputGroupAddon>
							<InputGroupInput
								autoCapitalize="none"
								autoComplete="username"
								id="email"
								inputMode="email"
								name="email"
								onChange={(e) => setEmail(e.target.value)}
								placeholder={t("emailPlaceholder")}
								required
								spellCheck={false}
								type="email"
								value={email}
							/>
						</InputGroup>
					</div>

					<div className="grid gap-2">
						<Label className={LABEL} htmlFor="password">
							{t("password")}
						</Label>
						<InputGroup className={FIELD}>
							<InputGroupAddon>
								<Lock aria-hidden="true" className={FIELD_ICON} />
							</InputGroupAddon>
							<InputGroupInput
								autoComplete="current-password"
								id="password"
								name="password"
								onChange={(e) => setPassword(e.target.value)}
								required
								type={showPassword ? "text" : "password"}
								value={password}
							/>
							<InputGroupAddon align="inline-end">
								<Button
									aria-label={showPassword ? t("hidePassword") : t("showPassword")}
									className="size-11 text-muted-foreground hover:text-foreground sm:size-11"
									onClick={() => setShowPassword((visible) => !visible)}
									size="icon"
									variant="ghost"
								>
									{showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
								</Button>
							</InputGroupAddon>
						</InputGroup>
					</div>

					{error && (
						<p
							className="flex items-start gap-2 text-pretty text-destructive-foreground text-sm"
							role="alert"
						>
							<CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
							{t(error)}
						</p>
					)}

					<Button
						className="mt-1 h-12 w-full rounded-full font-semibold text-base sm:h-12 sm:text-base"
						disabled={loading}
						type="submit"
					>
						{loading ? (
							<>
								<Loader2 aria-hidden="true" className="size-5 animate-spin" />
								{t("signingIn")}
							</>
						) : (
							t("signIn")
						)}
					</Button>
				</form>
			</div>
		</section>
	);
}
